import type { ReactElement } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { FullPageMessage } from '../components/FullPageMessage.js';
import { useAuth } from './AuthProvider.js';

/**
 * Gates the whole backoffice on an *admin* session.
 *
 * The API already refuses every `/admin` route to non-admins, so this guard is not the
 * security boundary — it exists so an interviewer who reuses their credentials here lands
 * on a clear "not an admin account" message rather than a dashboard of failed requests.
 */
export function RequireAdmin(): ReactElement {
  const { status, user, logout } = useAuth();
  const location = useLocation();

  if (status === 'bootstrapping') {
    return <FullPageMessage title="Restoring your session…" />;
  }

  if (status === 'anonymous' || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (user.role !== 'admin') {
    return (
      <FullPageMessage
        title="This account is not an administrator"
        detail="The backoffice is restricted to admin accounts. Interviewers should use the main app."
      >
        <button type="button" onClick={logout} className="btn-secondary">
          Sign in as someone else
        </button>
      </FullPageMessage>
    );
  }

  return <Outlet />;
}

/** Keeps a signed-in admin off /login. */
export function RedirectIfAuthenticated(): ReactElement {
  const { status, user } = useAuth();

  if (status === 'bootstrapping') {
    return <FullPageMessage title="Restoring your session…" />;
  }
  if (status === 'authenticated' && user?.role === 'admin') {
    return <Navigate to="/interviews" replace />;
  }
  return <Outlet />;
}
